import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, queryOptions } from "@tanstack/react-query";
import { BadgeCheck, Building2, Search } from "lucide-react";
import { listBusinessProfiles } from "@/lib/business.functions";
import { adImageSrc } from "@/lib/image-src";
import { Input } from "@/components/ui/input";

const businessesQuery = queryOptions({
  queryKey: ["businesses"],
  queryFn: () => listBusinessProfiles({ data: {} }),
  staleTime: 5 * 60 * 1000,
});

export const Route = createFileRoute("/foretag/lista")({
  loader: ({ context }) => context.queryClient.ensureQueryData(businessesQuery),
  head: () => {
    const title = "Företag som säljer på Sellavo – hela listan";
    const description =
      "Bläddra bland alla företag med profil på Sellavo. Se antal annonser, verifierade organisationsnummer och köpvillkor.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: "https://sellavo.com/foretag/lista" },
        { name: "twitter:card", content: "summary" },
      ],
      links: [{ rel: "canonical", href: "https://sellavo.com/foretag/lista" }],
    };
  },
  errorComponent: () => (
    <main className="mx-auto w-full max-w-4xl px-4 py-16 text-center sm:px-6 lg:px-8">
      <h1 className="text-2xl font-semibold">Vi kunde inte hämta företagen</h1>
    </main>
  ),
  notFoundComponent: () => (
    <main className="mx-auto w-full max-w-4xl px-4 py-16 text-center sm:px-6 lg:px-8">
      <h1 className="text-2xl font-semibold">Sidan finns inte</h1>
    </main>
  ),
  component: BusinessList,
});

function BusinessList() {
  const { data } = useQuery(businessesQuery);
  const [term, setTerm] = useState("");
  const rows = (data ?? []).filter((b) =>
    term.trim() ? b.name.toLowerCase().includes(term.trim().toLowerCase()) : true,
  );

  return (
    <main className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <h1 className="font-heading text-3xl font-bold tracking-tight">Företag på Sellavo</h1>
      <p className="mt-2 max-w-2xl text-muted-foreground">
        Alla företag med egen profil. Handlar du av ett företag gäller ångerrätt och
        reklamationsrätt.
      </p>

      <div className="relative mt-6 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Sök företag"
          className="pl-9"
          aria-label="Sök företag"
        />
      </div>

      {rows.length === 0 ? (
        <p className="mt-10 rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          Inga företag matchar sökningen än.
        </p>
      ) : (
        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {rows.map((b) => (
            <li key={b.slug}>
              <Link
                to="/foretag/$slug"
                params={{ slug: b.slug }}
                className="flex h-full items-start gap-3 rounded-xl border border-border bg-card p-4 transition hover:border-primary/50 hover:shadow-sm"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted">
                  {b.logoUrl ? (
                    <img src={adImageSrc(b.logoUrl, 200)} alt="" className="h-full w-full object-cover" loading="lazy" />
                  ) : (
                    <Building2 className="h-5 w-5 text-muted-foreground" />
                  )}
                </span>
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5 font-semibold">
                    <span className="truncate">{b.name}</span>
                    {b.verified && <BadgeCheck className="h-4 w-4 shrink-0 text-primary" />}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {b.adCount} {b.adCount === 1 ? "aktiv annons" : "aktiva annonser"}
                  </span>
                  {b.about && (
                    <span className="mt-1 line-clamp-2 block text-sm text-muted-foreground">
                      {b.about}
                    </span>
                  )}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
